import { FC, useEffect } from "react";
import {
  Button,
  Modal,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { useSnackbar } from "notistack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CaseReportsAPI from "@/api/cases/reports";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { CaseReportsQuery } from "../caseReports.query";

interface CaseReportRowData {
  case_report_id: number;
  name: string;
  description?: string | null;
}

interface EditCaseReportModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  caseId: string | number;
  defaultInfo: CaseReportRowData;
}

export const EditCaseReportModal: FC<EditCaseReportModalProps> = ({
  show,
  onClose,
  onSubmit,
  caseId,
  defaultInfo,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: {
      name: defaultInfo.name ?? "",
      description: defaultInfo.description ?? "",
    },
    validate: {
      name: (value) =>
        !value || value.trim() === "" ? "Case Report name is required" : null,
    },
  });

  useEffect(() => {
    form.setValues({
      name: defaultInfo.name ?? "",
      description: defaultInfo.description ?? "",
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultInfo.case_report_id]);

  const updateMutation = useMutation({
    mutationFn: (params: {
      case_id: string | number;
      case_report_id: number;
      name: string;
      description?: string;
    }) => CaseReportsAPI.updateCaseReport(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CaseReportsQuery.queryKeys.base,
      });

      enqueueSnackbar(`Case Report "${form.values.name}" updated`, {
        variant: "success",
      });

      LoggingAPI.createLog({
        case_id: caseId,
        user_id: currentUser.user_id,
        message: `Updated case report: ${form.values.name}.`,
        event: "update_case_report",
        object_type: "case_report",
      });

      onSubmit?.();
      onClose();
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while updating this case report. Please try again.",
        { variant: "error" }
      );
    },
  });

  const handleSubmit = () => {
    if (!form.validate().hasErrors) {
      updateMutation.mutate({
        case_id: caseId,
        case_report_id: defaultInfo.case_report_id,
        name: form.values.name,
        description: form.values.description,
      });
    }
  };

  const handleCancel = () => {
    form.setValues({
      name: defaultInfo.name ?? "",
      description: defaultInfo.description ?? "",
    });
    form.resetDirty();
    onClose();
  };

  return (
    <Modal open={show} onClose={handleCancel}>
      <FormWrapper width={500}>
        <FormTitle>Edit Case Report</FormTitle>
        <TextInput
          size="sm"
          variant="outlined"
          label="Case Report Name"
          required
          placeholder="Enter case report name"
          {...form.getInputProps("name")}
        />
        <TextAreaInput
          size="sm"
          variant="outlined"
          label="Description"
          placeholder="Enter a description for this report"
          minRows={4}
          maxRows={10}
          {...form.getInputProps("description")}
        />
        <ButtonMenu>
          <Button size="xs" variant="subtle" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
          >
            Save Changes
          </Button>
        </ButtonMenu>
      </FormWrapper>
    </Modal>
  );
};
